import { Page } from '@roc/feature-app-core';
import {
  BorrowerDrawDetailsModal,
  BorrowerDrawRequest,
} from '@roc/feature-borrower-draws';
import { useDrawsStore } from '@roc/feature-draws';
import {
  BorrowerDocumentForms,
  DocumentName,
  useDocumentStore,
} from '@roc/feature-documents';
import { CustomTaskDialog, TaskStatusDB } from '@roc/feature-general-tasks';
import { LineItemUpload, TodoActions } from '@roc/feature-loan-details';
import {
  ExtensionDetailsModal,
  ExtensionRequestModal,
  PayoffDetailsDialog,
  RequestPayoffDialog,
  RetractPayoffDialog,
  usePayoffRequestStore,
} from '@roc/feature-loans';
import { ScopeOfWorkV2FormDialog } from '@roc/feature-sow-v2';
import { useScopeOfWorkV2Store } from '@roc/feature-sow-shared/hooks';
import {
  CapitalProvider,
  DRAWS,
  LoanProcess,
  getExtensionId,
  shouldHideTaskUploadTab,
} from '@roc/feature-utils';
import {
  ConfirmDialog,
  DropdownMenu,
  FileUploadModal,
  PDFPreview,
  StandardDialog,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Route, useHistory, useParams } from 'react-router-dom';
import { useBorrowerLoansStore } from '../../../hooks/useBorrowerLoansStore';
import { useBorrowerLoansRoutes } from '../../../hooks/useBorrowerLoansRoutes';
import { useBorrowerLoanApplicationsStore } from '../../../hooks/useBorrowerLoanApplicationsStore';
import { BorrowerPropertiesModal } from '../properties/borrowerProperties';
import { RETRACTED_STAGE } from 'libs/feature-loans/src/loans/loanDetails/payoffs/constants/payoffs';
import {
  Cancel,
  CloudDownload,
  Create,
  InsertDriveFile,
  Launch,
  MoreVert,
  Publish,
} from '@material-ui/icons';
import SOWRevisionRequestDialog from 'libs/feature-draws/src/draws/reviseScopeOfWork/components/SOWRevisionRequestDialog';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'platform-ui': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        id: string;
        component: string;
        [key: string]: any;
      };
    }
  }
}

enum PlatformUiBorrowerLoanEvent {
  REQUEST_DRAW = 'platform-ui:borrower-loan:request-draw',
  VIEW_SOW = 'platform-ui:borrower-loan:view-sow',
  OPEN_DRAW = 'platform-ui:borrower-loan:open-draw',
  OPEN_PROPERTY = 'platform-ui:borrower-loan:open-property',
  OPEN_EXTENSION = 'platform-ui:borrower-loan:open-extension',
  REQUEST_EXTENSION = 'platform-ui:borrower-loan:request-extension',
  REQUEST_PAYOFF = 'platform-ui:borrower-loan:request-payoff',
  OPEN_PAYOFF = 'platform-ui:borrower-loan:open-payoff',
  OPEN_TASK = 'platform-ui:borrower-loan:open-task',
  OPEN_TASK_ACTIONS = 'platform-ui:borrower-loan:open-task-actions',
  OPEN_SOW_ACTIONS = 'platform-ui:borrower-loan:open-sow-actions',
  OPEN_SOW_REVISION_ACTIONS = 'platform-ui:borrower-loan:open-sow-revision-actions',
  OPEN_DRAW_ACTIONS = 'platform-ui:borrower-loan:open-draw-actions',
  OPEN_PAYOFF_ACTIONS = 'platform-ui:borrower-loan:open-payoff-actions',
}

function BorrowerLoanOverviewPlatformUi({
  loanId,
  initialTab,
  source,
}: {
  loanId: string;
  initialTab?: string;
  source?: string;
}) {

  return (
    <platform-ui
      id="platform-ui-borrower-loan-view"
      component="borrower_loan_view"
      data-loan-number={loanId}
      data-tab={initialTab}
      data-loan-source={source}
    />
  );
}

export const BorrowerLoanDetailsChildRoutesV3 = observer(
  ({ isPlatformLoan }: { isPlatformLoan: boolean }) => {
    // The roc-native route param is `:loanId`, the platform route param is `:loanNumber` (see
    // borrowerLoanRoutes.tsx/platformLoanRoutesConfig) — fall back to whichever one is present.
    const params = useParams<{
      loanId: string;
      loanNumber: string;
      drawId: string;
    }>();
    const loanId = params.loanId || params.loanNumber;
    const { drawId } = params;
    const { push } = useHistory();
    const { borrowerLoansStore } = useBorrowerLoansStore();
    const { loanDetails } = borrowerLoansStore;
    const { payoffRequestStore } = usePayoffRequestStore();
    const { documentStore } = useDocumentStore();
    const { scopeOfWorkV2Store } = useScopeOfWorkV2Store();
    const { scopeOfWorkRevisionStore } = useDrawsStore();
    const {
      borrowerLoanApplicationsStore,
    } = useBorrowerLoanApplicationsStore();
    const {
      borrowerLoanRoutesConfig: loanRoutesConfig,
      platformBorrowerLoanRoutesConfig: platformLoanRoutesConfig,
    } = useBorrowerLoansRoutes();
    const { pathname } = useLocation<{ from?: string }>();

    const activeLoanRoutesConfig = isPlatformLoan
      ? platformLoanRoutesConfig
      : loanRoutesConfig;
    const loanDetailsChildRoutes = activeLoanRoutesConfig.loans(loanId)
      .children;

    const source = isPlatformLoan
      ? CapitalProvider.EXTERNAL_CAPITAL
      : CapitalProvider.PLATFORM_CAPITAL;

    const platformUiTabRouteUrls: Record<string, string> = {
      'loan-summary': loanDetailsChildRoutes.dashboard.url,
      tasks: isPlatformLoan
        ? platformLoanRoutesConfig.loans(loanId).children.tasks.url
        : loanRoutesConfig.loans(loanId).children.borrowerTodos.url,
      draws: loanDetailsChildRoutes.draws.url,
      extensions: loanDetailsChildRoutes.extensions.url,
      payoffs: loanDetailsChildRoutes.payoffRequest.url,
    };
    let platformUiInitialTab: string | undefined;
    Object.entries(platformUiTabRouteUrls).forEach(([tab, url]) => {
      if (pathname.indexOf(url) !== -1) {
        platformUiInitialTab = tab;
      }
    });
    const [uploadTaskDocument, setUploadTaskDocument] = useState<any>(null);
    const [taskActionsDocument, setTaskActionsDocument] = useState<any>(null);
    const [taskActionsPosition, setTaskActionsPosition] = useState({
      clientX: 0,
      clientY: 0,
    });
    const taskActionsRef = useRef<HTMLDivElement>(null);
    const [propertyModalOpen, setPropertyModalOpen] = useState(false);
    const [propertyModalProperty, setPropertyModalProperty] = useState<any>(
      null
    );
    const [sowActionsDocument, setSowActionsDocument] = useState<any>(null);
    const [sowActionsPosition, setSowActionsPosition] = useState({
      clientX: 0,
      clientY: 0,
    });
    const sowActionsRef = useRef<HTMLDivElement>(null);
    const [
      sowRevisionActionsDocument,
      setSowRevisionActionsDocument,
    ] = useState<any>(null);
    const [
      sowRevisionActionsPosition,
      setSowRevisionActionsPosition,
    ] = useState({
      clientX: 0,
      clientY: 0,
    });
    const sowRevisionActionsRef = useRef<HTMLDivElement>(null);
    const [drawActionsDrawId, setDrawActionsDrawId] = useState<{
      drawId: number;
      _key: number;
    } | null>(null);
    const [drawActionsPosition, setDrawActionsPosition] = useState({
      clientX: 0,
      clientY: 0,
    });
    const drawActionsRef = useRef<HTMLDivElement>(null);
    const [payoffActionsPayoff, setPayoffActionsPayoff] = useState<{
      payoffStatus: string;
      _key: number;
    } | null>(null);
    const [payoffActionsPosition, setPayoffActionsPosition] = useState({
      clientX: 0,
      clientY: 0,
    });
    const payoffActionsRef = useRef<HTMLDivElement>(null);
    const [drawRequestOpen, setDrawRequestOpen] = useState(false);
    const [extensionRequestOpen, setExtensionRequestOpen] = useState(false);

    const prevPathnameRef = useRef(pathname);
    useEffect(() => {
      if (
        drawRequestOpen &&
        pathname !== prevPathnameRef.current &&
        pathname.indexOf(loanDetailsChildRoutes.draws.url) !== -1
      ) {
        setDrawRequestOpen(false);
      }
      prevPathnameRef.current = pathname;
    }, [pathname, loanDetailsChildRoutes, drawRequestOpen]);

    const notifyDrawsChanged = useCallback(() => {
      window.dispatchEvent(
        new CustomEvent('platform-ui:borrower-loan:draws-changed', {
          detail: { loanId: Number(loanId) },
        })
      );
    }, [loanId]);

    const prevDrawIdRef = useRef(drawId);
    useEffect(() => {
      if (prevDrawIdRef.current && !drawId) {
        notifyDrawsChanged();
      }
      prevDrawIdRef.current = drawId;
    }, [drawId, notifyDrawsChanged]);

    const notifyTasksChanged = useCallback(() => {
      window.dispatchEvent(
        new CustomEvent('platform-ui:borrower-loan:tasks-changed', {
          detail: { loanId: Number(loanId) },
        })
      );
    }, [loanId]);

    const notifySowChanged = useCallback(() => {
      window.dispatchEvent(
        new CustomEvent('platform-ui:borrower-loan:sow-changed', {
          detail: { loanId: Number(loanId) },
        })
      );
    }, [loanId]);

    const currentForm = documentStore.documentFormStore?.currentForm;
    const prevCurrentFormRef = useRef(currentForm);
    useEffect(() => {
      if (prevCurrentFormRef.current && !currentForm) {
        notifyTasksChanged();
      }
      prevCurrentFormRef.current = currentForm;
    }, [currentForm, notifyTasksChanged]);

    const onUploadTaskDocument = async (file: File, document: any) => {
      await documentStore.uploadDocument(
        file,
        document,
        LoanProcess.UNDERWRITING,
        null,
        document.drawId
      );
      setUploadTaskDocument(null);
      notifyTasksChanged();
    };

    useEffect(() => {
      if (taskActionsDocument) {
        const button = taskActionsRef.current?.querySelector('button');
        button?.click();
      }
    }, [taskActionsDocument]);

    useEffect(() => {
      if (sowActionsDocument) {
        const button = sowActionsRef.current?.querySelector('button');
        button?.click();
      }
    }, [sowActionsDocument]);

    useEffect(() => {
      if (sowRevisionActionsDocument) {
        const button = sowRevisionActionsRef.current?.querySelector('button');
        button?.click();
      }
    }, [sowRevisionActionsDocument]);

    useEffect(() => {
      if (drawActionsDrawId) {
        const button = drawActionsRef.current?.querySelector('button');
        button?.click();
      }
    }, [drawActionsDrawId]);

    useEffect(() => {
      if (payoffActionsPayoff) {
        const button = payoffActionsRef.current?.querySelector('button');
        button?.click();
      }
    }, [payoffActionsPayoff]);

    useEffect(() => {
      const isSameLoan = (detail: { loanId?: number | string }) =>
        String(detail?.loanId) === String(loanId);

      const handleRequestDraw = (e: Event) => {
        const { detail } = e as CustomEvent<{ loanId: number }>;
        if (isSameLoan(detail) && loanDetails?.showDraws) {
          setDrawRequestOpen(true);
        }
      };
      const handleViewSow = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          document: any;
        }>;
        if (!isSameLoan(detail) || !detail?.document || !loanDetails?.showDraws)
          return;
        if (!detail.document.dropboxPath) {
          scopeOfWorkV2Store.openScopeOfWork(detail.document);
        } else {
          documentStore.downloadDocument(detail.document);
        }
      };
      const handleOpenDraw = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          drawId: number;
        }>;
        if (isSameLoan(detail) && loanDetails?.showDraws && detail?.drawId) {
          push(loanDetailsChildRoutes.drawDetails(String(detail.drawId)).url);
        }
      };
      const handleOpenProperty = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          addressLine1: string;
        }>;
        if (!isSameLoan(detail) || !detail?.addressLine1) return;
        const property = (loanDetails?.properties ?? []).find((p: any) =>
          p?.address?.includes(detail.addressLine1)
        );
        if (property) {
          setPropertyModalProperty(property);
          setPropertyModalOpen(true);
        }
      };
      const handleOpenExtension = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          extensionId: number;
        }>;
        if (
          isSameLoan(detail) &&
          loanDetails?.showExtensions &&
          detail?.extensionId
        ) {
          push(
            activeLoanRoutesConfig
              .loans(loanId)
              .children.extensionDetails(String(detail.extensionId)).url
          );
        }
      };
      const handleOpenPayoff = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          payoffId: number;
        }>;
        if (
          isSameLoan(detail) &&
          loanDetails?.showPayoffs &&
          detail?.payoffId
        ) {
          payoffRequestStore.setSelectedPayoffId(detail.payoffId);
          payoffRequestStore.setIsPayoffDetailsModalOpen(true);
        }
      };
      const handleRequestExtension = (e: Event) => {
        const { detail } = e as CustomEvent<{ loanId: number }>;
        if (isSameLoan(detail) && loanDetails?.showExtensions) {
          setExtensionRequestOpen(true);
        }
      };
      const handleRequestPayoff = (e: Event) => {
        const { detail } = e as CustomEvent<{ loanId: number }>;
        if (isSameLoan(detail) && loanDetails?.showPayoffs) {
          payoffRequestStore.setIsNewPayoffRequestModalOpen(true);
        }
      };
      const handleOpenTask = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          document: any;
        }>;
        if (!isSameLoan(detail) || !detail?.document) return;
        const document = documentStore.formatDocument(
          detail.document,
          LoanProcess.UNDERWRITING
        );
        if (document.manualTask || (document.isForm && document.canOpenForm)) {
          documentStore.onOpenForm(document);
        } else if (document.canUploadFile) {
          setUploadTaskDocument(document);
        } else if (document.canDownloadFile) {
          const isPDF = document.dropboxPath?.endsWith('.pdf');
          if (isPDF) {
            documentStore.previewDocument(document);
          } else {
            documentStore.downloadDocument(document);
          }
        }
      };
      const handleOpenTaskActions = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          document: any;
          clientX: number;
          clientY: number;
        }>;
        if (!isSameLoan(detail) || !detail?.document) return;
        const document = documentStore.formatDocument(
          detail.document,
          LoanProcess.UNDERWRITING
        );
        setTaskActionsPosition({
          clientX: detail.clientX,
          clientY: detail.clientY,
        });
        setTaskActionsDocument({ ...document, _key: Date.now() });
      };
      const handleOpenSowActions = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          document: any;
          clientX: number;
          clientY: number;
        }>;
        if (!isSameLoan(detail) || !detail?.document) return;
        setSowActionsPosition({
          clientX: detail.clientX,
          clientY: detail.clientY,
        });
        setSowActionsDocument({ ...detail.document, _key: Date.now() });
      };
      const handleOpenSowRevisionActions = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          document: any;
          clientX: number;
          clientY: number;
        }>;
        if (!isSameLoan(detail) || !detail?.document) return;
        setSowRevisionActionsPosition({
          clientX: detail.clientX,
          clientY: detail.clientY,
        });
        setSowRevisionActionsDocument({ ...detail.document, _key: Date.now() });
      };
      const handleOpenDrawActions = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          drawId: number;
          clientX: number;
          clientY: number;
        }>;
        if (!isSameLoan(detail) || !detail?.drawId) return;
        setDrawActionsPosition({
          clientX: detail.clientX,
          clientY: detail.clientY,
        });
        setDrawActionsDrawId({ drawId: detail.drawId, _key: Date.now() });
      };
      const handleOpenPayoffActions = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          payoffStatus: string;
          clientX: number;
          clientY: number;
        }>;
        if (!isSameLoan(detail) || !detail?.payoffStatus) return;
        setPayoffActionsPosition({
          clientX: detail.clientX,
          clientY: detail.clientY,
        });
        setPayoffActionsPayoff({
          payoffStatus: detail.payoffStatus,
          _key: Date.now(),
        });
      };

      window.addEventListener(
        PlatformUiBorrowerLoanEvent.REQUEST_DRAW,
        handleRequestDraw
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.VIEW_SOW,
        handleViewSow
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_DRAW,
        handleOpenDraw
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_PROPERTY,
        handleOpenProperty
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_EXTENSION,
        handleOpenExtension
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.REQUEST_EXTENSION,
        handleRequestExtension
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.REQUEST_PAYOFF,
        handleRequestPayoff
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_PAYOFF,
        handleOpenPayoff
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_TASK,
        handleOpenTask
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_TASK_ACTIONS,
        handleOpenTaskActions
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_SOW_ACTIONS,
        handleOpenSowActions
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_SOW_REVISION_ACTIONS,
        handleOpenSowRevisionActions
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_DRAW_ACTIONS,
        handleOpenDrawActions
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_PAYOFF_ACTIONS,
        handleOpenPayoffActions
      );
      return () => {
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.REQUEST_DRAW,
          handleRequestDraw
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.VIEW_SOW,
          handleViewSow
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_DRAW,
          handleOpenDraw
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_PROPERTY,
          handleOpenProperty
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_EXTENSION,
          handleOpenExtension
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.REQUEST_EXTENSION,
          handleRequestExtension
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.REQUEST_PAYOFF,
          handleRequestPayoff
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_PAYOFF,
          handleOpenPayoff
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_TASK,
          handleOpenTask
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_TASK_ACTIONS,
          handleOpenTaskActions
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_SOW_ACTIONS,
          handleOpenSowActions
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_SOW_REVISION_ACTIONS,
          handleOpenSowRevisionActions
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_DRAW_ACTIONS,
          handleOpenDrawActions
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_PAYOFF_ACTIONS,
          handleOpenPayoffActions
        );
      };
    }, [
      loanId,
      loanDetails,
      documentStore,
      push,
      loanDetailsChildRoutes,
      activeLoanRoutesConfig,
      payoffRequestStore,
      scopeOfWorkV2Store,
    ]);

    const renderExtensionDetails = () => {
      const extensionId = getExtensionId(pathname);
      return loanDetails?.showExtensions ? (
        <Route
          exact
          path={
            activeLoanRoutesConfig
              .loans(loanId)
              .children.extensionDetails(extensionId).path
          }
        >
          <Page
            routeDefinition={activeLoanRoutesConfig
              .loans(loanId)
              .children.extensionDetails(extensionId)}
          >
            <ExtensionDetailsModal loanId={loanId} extensionId={extensionId} />
          </Page>
        </Route>
      ) : null;
    };

    if (!loanDetails || '' + loanDetails?.loanId !== loanId) {
      return null;
    }

    return (
      <>
        <BorrowerLoanOverviewPlatformUi
          loanId={loanId}
          initialTab={platformUiInitialTab}
          source={source}
        />
        {loanDetails?.showDraws && drawId && (
          <Route exact path={loanDetailsChildRoutes.drawDetails(drawId).path}>
            <Page routeDefinition={loanDetailsChildRoutes.drawDetails(drawId)}>
              <BorrowerDrawDetailsModal loanId={loanId} drawId={drawId} />
            </Page>
          </Route>
        )}
        {renderExtensionDetails()}
        <RequestPayoffDialog />
        <PayoffDetailsDialog loanId={loanId} />
        <RetractPayoffDialog
          loanId={loanId}
          loanPayoffDataId={payoffRequestStore.currentPayoff?.loanPayoffDataId}
        />
        {!isPlatformLoan && (
          <BorrowerDocumentForms
            loanId={loanId}
            isLoanApplication={false}
            borrowerLoanApplicationsStore={borrowerLoanApplicationsStore}
          />
        )}
        <BorrowerPropertiesModal
          open={propertyModalOpen}
          loanSubtype={loanDetails?.loanSubType}
          property={propertyModalProperty}
          handleClose={() => {
            setPropertyModalOpen(false);
            setPropertyModalProperty(null);
          }}
        />
        <PDFPreview
          open={documentStore.documentPreview != null}
          title={documentStore.documentPreview?.title}
          data={documentStore.documentPreview?.data}
          dataV2={documentStore.documentPreview?.data}
          headers={documentStore.documentPreview?.headers}
          onClose={() => documentStore.closeDocumentPreview()}
        />
        {uploadTaskDocument &&
          (uploadTaskDocument.taskId ? (
            <CustomTaskDialog
              title={uploadTaskDocument.taskName}
              taskId={uploadTaskDocument.taskId}
              open
              dialogContent={
                shouldHideTaskUploadTab(uploadTaskDocument) ? null : (
                  <LineItemUpload document={uploadTaskDocument} expand />
                )
              }
              handleClose={() => {
                setUploadTaskDocument(null);
                notifyTasksChanged();
              }}
            />
          ) : (
            <FileUploadModal
              title={`Upload: ${uploadTaskDocument.taskName}`}
              open
              onClose={() => setUploadTaskDocument(null)}
              onUpload={uploads =>
                onUploadTaskDocument(uploads[0].file, uploadTaskDocument)
              }
            />
          ))}
        {taskActionsDocument && (
          <div
            ref={taskActionsRef}
            style={{
              position: 'fixed',
              left: taskActionsPosition.clientX,
              top: taskActionsPosition.clientY,
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <TodoActions
              key={taskActionsDocument._key}
              document={taskActionsDocument}
              loanProcess={LoanProcess.UNDERWRITING}
              onClose={() => {
                setTaskActionsDocument(null);
                notifyTasksChanged();
              }}
            />
          </div>
        )}
        {sowActionsDocument && (
          <div
            ref={sowActionsRef}
            style={{
              position: 'fixed',
              left: sowActionsPosition.clientX,
              top: sowActionsPosition.clientY,
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <SOWDocumentActions
              key={sowActionsDocument._key}
              document={sowActionsDocument}
              onSowChanged={notifySowChanged}
            />
          </div>
        )}
        {sowRevisionActionsDocument && (
          <div
            ref={sowRevisionActionsRef}
            style={{
              position: 'fixed',
              left: sowRevisionActionsPosition.clientX,
              top: sowRevisionActionsPosition.clientY,
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <SOWRevisionActions
              key={sowRevisionActionsDocument._key}
              revision={sowRevisionActionsDocument}
              loanId={loanId}
              onSowChanged={notifySowChanged}
            />
          </div>
        )}
        {drawActionsDrawId && (
          <div
            ref={drawActionsRef}
            style={{
              position: 'fixed',
              left: drawActionsPosition.clientX,
              top: drawActionsPosition.clientY,
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <DrawActions
              key={drawActionsDrawId._key}
              drawId={drawActionsDrawId.drawId}
              loanId={loanId}
            />
          </div>
        )}
        {payoffActionsPayoff && (
          <div
            ref={payoffActionsRef}
            style={{
              position: 'fixed',
              left: payoffActionsPosition.clientX,
              top: payoffActionsPosition.clientY,
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <PayoffActions
              key={payoffActionsPayoff._key}
              payoffStatus={payoffActionsPayoff.payoffStatus}
            />
          </div>
        )}
        <StandardDialog
          open={drawRequestOpen}
          fullScreen
          removeContentBox
          handleClose={() => {
            setDrawRequestOpen(false);
            notifyDrawsChanged();
          }}
          dialogContent={<BorrowerDrawRequest hideBackButton />}
        />
        <StandardDialog
          open={extensionRequestOpen}
          title=""
          maxWidth="md"
          handleClose={() => setExtensionRequestOpen(false)}
          dialogContent={
            <ExtensionRequestModal
              closeModal={() => setExtensionRequestOpen(false)}
            />
          }
        />
        <ScopeOfWorkV2FormDialog
          open={scopeOfWorkV2Store.scopeOfWorkV2FormDialogOpen}
          onClose={() =>
            scopeOfWorkV2Store.setScopeOfWorkV2FormDialogOpen(false)
          }
          store={scopeOfWorkV2Store.scopeOfWorkV2FormStore}
        />
        <ScopeOfWorkV2FormDialog
          title="Scope Of Work Revision"
          open={scopeOfWorkRevisionStore.scopeOfWorkV2FormDialogOpen}
          onClose={() =>
            scopeOfWorkRevisionStore.setScopeOfWorkV2FormDialogOpen(false)
          }
          store={scopeOfWorkRevisionStore.scopeOfWorkV2FormStore}
        />
      </>
    );
  }
);

export const DrawActions = observer(
  ({
    drawId,
    loanId,
  }: {
    drawId: number | string;
    loanId: number | string;
  }) => {
    const { drawStore } = useDrawsStore();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const cancelRequest = () => {
      drawStore.cancelDrawRequest(Number(drawId), String(loanId));
      setIsConfirmOpen(false);
    };

    const options = [
      {
        icon: Cancel,
        name: 'Cancel Request',
        action: () => setIsConfirmOpen(true),
      },
    ];

    return (
      <>
        <DropdownMenu options={options}>
          <MoreVert color="primary" />
        </DropdownMenu>
        <ConfirmDialog
          open={isConfirmOpen}
          title="Cancel Draw Request"
          body={
            'Are you sure you want to cancel the draw request?\n\nThis Operation cannot be reverted.'
          }
          handleCancel={() => setIsConfirmOpen(false)}
          handleClose={() => setIsConfirmOpen(false)}
          handleConfirm={cancelRequest}
          confirmButtonText="Confirm"
        />
      </>
    );
  }
);

export const PayoffActions = observer(
  ({ payoffStatus }: { payoffStatus: string }) => {
    const { payoffRequestStore } = usePayoffRequestStore();

    if (payoffStatus === RETRACTED_STAGE) {
      return null;
    }

    const options = [
      {
        name: 'Retract Payoff',
        action: () => payoffRequestStore.setIsNewPayoffRetractModalOpen(true),
      },
    ];

    return (
      <DropdownMenu options={options}>
        <MoreVert />
      </DropdownMenu>
    );
  }
);

export const SOWDocumentActions = observer(
  ({
    document,
    onSowChanged,
  }: {
    document: any;
    onSowChanged?: () => void;
  }) => {
    const { scopeOfWorkRevisionStore } = useDrawsStore();
    const { documentStore } = useDocumentStore();
    const { scopeOfWorkV2Store } = useScopeOfWorkV2Store();
    const [
      openSOWRevisionRequestDialog,
      setOpenSOWRevisionRequestDialog,
    ] = useState(false);

    const options = [
      ...(!document?.dropboxPath
        ? [
            {
              icon: InsertDriveFile,
              name: 'View Document',
              action: () => scopeOfWorkV2Store.openScopeOfWork(document),
            },
          ]
        : [
            {
              icon: CloudDownload,
              name: 'Download Document',
              action: () => documentStore.downloadDocument(document),
            },
          ]),
      {
        icon: Create,
        name: 'Revise Scope of Work',
        action: () => setOpenSOWRevisionRequestDialog(true),
      },
    ];

    return (
      <>
        <DropdownMenu options={options}>
          <MoreVert color="primary" />
        </DropdownMenu>
        <StandardDialog
          open={openSOWRevisionRequestDialog}
          title="SOW Revision Request"
          maxWidth="sm"
          handleClose={() => setOpenSOWRevisionRequestDialog(false)}
          dialogContent={
            <SOWRevisionRequestDialog
              onClose={() => setOpenSOWRevisionRequestDialog(false)}
              onContinue={() => {
                Promise.resolve(
                  scopeOfWorkRevisionStore.submitSOWRevision(document)
                ).then(() => onSowChanged?.());
                setOpenSOWRevisionRequestDialog(false);
              }}
            />
          }
        />
      </>
    );
  }
);

export const SOWRevisionActions = observer(
  ({
    revision,
    loanId,
    onSowChanged,
  }: {
    revision: any;
    loanId: string | number;
    onSowChanged?: () => void;
  }) => {
    const { drawStore, scopeOfWorkRevisionStore } = useDrawsStore();
    const { documentStore } = useDocumentStore();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const onUpload = useCallback(() => {
      inputRef.current?.click();
    }, []);

    const cancelRevision = () => {
      Promise.resolve(drawStore.cancelRevision(revision)).then(() =>
        onSowChanged?.()
      );
      setIsConfirmOpen(false);
    };

    const options = [
      ...(revision?.taskName?.startsWith(DocumentName.REVISED_SOW_FORM + ' - ')
        ? [
            {
              icon: Launch,
              name: 'Open Form',
              action: () => scopeOfWorkRevisionStore.fetchSOWRevision(revision),
              disabled: revision?.status !== TaskStatusDB.PENDING && revision?.status !== TaskStatusDB.MORE_INFO_NEEDED,
            },
          ]
        : []),
      ...(revision?.taskName?.startsWith(DocumentName.REVISED_SOW + ' - ')
        ? [
            {
              icon: CloudDownload,
              name: 'Download Document',
              action: () => documentStore.downloadDocument(revision),
              disabled: !revision.dropboxPath,
            },
            {
              icon: Publish,
              name: 'Upload Document',
              action: onUpload,
              disabled: revision?.status !== TaskStatusDB.PENDING,
            },
          ]
        : []),
      ...(revision?.status === TaskStatusDB.PENDING
        ? [
            {
              icon: Cancel,
              name: 'Cancel Revision',
              action: () => setIsConfirmOpen(true),
            },
          ]
        : []),
    ];

    return (
      <>
        <input
          accept="*"
          type="file"
          hidden
          ref={inputRef}
          onChange={event => {
            const file = event.target.files?.[0];
            if (file) {
              documentStore.uploadDocument(file, revision, DRAWS, () => {
                drawStore.fetchSOWDocuments(String(loanId));
                onSowChanged?.();
              });
            }
          }}
        />
        <ConfirmDialog
          open={isConfirmOpen}
          title="Cancel SOW Revision"
          body={
            'Are you sure you want to cancel the SOW revision?\n\nThis Operation cannot be reverted.'
          }
          handleCancel={() => setIsConfirmOpen(false)}
          handleClose={() => setIsConfirmOpen(false)}
          handleConfirm={cancelRevision}
          confirmButtonText="Confirm"
        />
        <DropdownMenu options={options}>
          <MoreVert color="primary" />
        </DropdownMenu>
      </>
    );
  }
);
